import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspaces } from '@/hooks/useWorkspaces';

// Chatwoot configuration
const CHATWOOT_BASE_URL = 'https://chatwoot-production-bc5c.up.railway.app';
const CHATWOOT_WEBSITE_TOKEN = 'kTdpgR3cGX1xemWpoYfHEnnh';

// TypeScript declarations for Chatwoot SDK
declare global {
  interface Window {
    chatwootSettings?: {
      hideMessageBubble?: boolean;
      position?: 'left' | 'right';
      locale?: string;
      type?: 'standard' | 'expanded_bubble';
    };
    chatwootSDK?: {
      run: (config: { websiteToken: string; baseUrl: string }) => void;
    };
    $chatwoot?: {
      setUser: (identifier: string, user: {
        email?: string;
        name?: string;
        avatar_url?: string;
        phone_number?: string;
        description?: string;
        country_code?: string;
        city?: string;
        company_name?: string;
        identifier_hash?: string;
      }) => void;
      setCustomAttributes: (attributes: Record<string, unknown>) => void;
      setConversationCustomAttributes: (attributes: Record<string, unknown>) => void;
      deleteConversationCustomAttribute: (key: string) => void;
      setLabel: (label: string) => void;
      removeLabel: (label: string) => void;
      setLocale: (locale: string) => void;
      reset: () => void;
      toggle: (state?: 'open' | 'close') => void;
    };
  }
}

export function ChatwootWidget() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspaces();

  // Load Chatwoot SDK script
  useEffect(() => {
    // Only load if not already present
    if (document.getElementById('chatwoot-sdk')) return;

    // Set widget settings before loading
    window.chatwootSettings = {
      hideMessageBubble: false,
      position: 'right',
      locale: 'en',
      type: 'standard',
    };

    // Create and inject script
    const script = document.createElement('script');
    script.id = 'chatwoot-sdk';
    script.src = `${CHATWOOT_BASE_URL}/packs/js/sdk.js`;
    script.async = true;
    script.onload = () => {
      window.chatwootSDK?.run({
        websiteToken: CHATWOOT_WEBSITE_TOKEN,
        baseUrl: CHATWOOT_BASE_URL,
      });
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup on unmount (though typically won't unmount)
      const existingScript = document.getElementById('chatwoot-sdk');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Set user context when authenticated
  useEffect(() => {
    const handleChatwootReady = () => {
      if (!window.$chatwoot) return;

      if (user) {
        // Identify the user
        window.$chatwoot.setUser(user.id, {
          email: user.email || undefined,
          name: user.user_metadata?.full_name || user.email || 'User',
          avatar_url: user.user_metadata?.avatar_url || undefined,
        });

        // Set contact-level attributes
        window.$chatwoot.setCustomAttributes({
          userId: user.id,
          workspaceId: currentWorkspace?.id || 'none',
          workspaceName: currentWorkspace?.name || 'No workspace',
          signupDate: user.created_at,
        });

        // Set conversation context
        window.$chatwoot.setConversationCustomAttributes({
          currentPage: window.location.pathname,
          userAgent: navigator.userAgent,
          screenSize: `${window.innerWidth}x${window.innerHeight}`,
          appUrl: window.location.origin,
        });
      }
    };

    // Listen for SDK ready event
    window.addEventListener('chatwoot:ready', handleChatwootReady);

    // Also try immediately in case SDK already loaded
    if (window.$chatwoot && user) {
      handleChatwootReady();
    }

    return () => {
      window.removeEventListener('chatwoot:ready', handleChatwootReady);
    };
  }, [user, currentWorkspace]);

  // Reset Chatwoot on logout
  useEffect(() => {
    if (!user && window.$chatwoot) {
      window.$chatwoot.reset();
    }
  }, [user]);

  // Update current page on navigation
  useEffect(() => {
    if (window.$chatwoot && user) {
      window.$chatwoot.setConversationCustomAttributes({
        currentPage: window.location.pathname,
      });
    }
  }, [user]);

  // This component doesn't render anything - the SDK handles the widget
  return null;
}
